import React from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, Stack, Typography } from '@mui/material';
import { purple } from '@mui/material/colors';

import { ConflictInfo, ConflictResolutionStatus } from '~/types/stopsell';

import { formatDateRange } from './StopsellRoomGroup';

const PARTIAL_OVERLAP_COLORS = {
  bgcolor: purple[50],
  borderColor: purple[200],
  iconColor: purple[800],
  textColor: purple[900],
  hoverBorderColor: purple[400],
} as const;

interface Props {
  conflict: ConflictInfo;
  onResolve: (status: ConflictResolutionStatus) => void;
}

export default function StopsellConflictBanner({ conflict, onResolve }: Props) {
  const existingLabel = formatDateRange(conflict.existingRange.start, conflict.existingRange.end);
  const mergedLabel = conflict.mergedRange ? formatDateRange(conflict.mergedRange.start, conflict.mergedRange.end) : '';

  const config = {
    covered_by_existing: {
      bgcolor: 'warning.50',
      borderColor: 'warning.200',
      icon: <WarningAmberIcon sx={{ fontSize: 15, color: 'warning.main', flexShrink: 0, mt: '1px' }} />,
      text: `Entirely covered by existing blackout ${existingLabel}. These new dates can be safely ignored.`,
      actions: (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onResolve('ignored')}
          sx={{
            fontSize: 11,
            borderColor: 'warning.400',
            color: 'warning.dark',
            '&:hover': { borderColor: 'warning.600', bgcolor: 'warning.50' },
          }}
        >
          Ignore new dates
        </Button>
      ),
    },
    covers_existing: {
      bgcolor: 'info.50',
      borderColor: 'info.200',
      icon: <InfoOutlinedIcon sx={{ fontSize: 15, color: 'info.main', flexShrink: 0, mt: '1px' }} />,
      text: `This range fully covers existing blackout ${existingLabel}. You can override the existing dates with this wider range.`,
      actions: (
        <Button
          size="small"
          variant="outlined"
          onClick={() => onResolve('overriding')}
          sx={{
            fontSize: 11,
            borderColor: 'info.200',
            color: 'info.dark',
            '&:hover': { borderColor: 'info.300', bgcolor: 'info.50' },
          }}
        >
          Override existing dates
        </Button>
      ),
    },
    partial_overlap: {
      bgcolor: PARTIAL_OVERLAP_COLORS.bgcolor,
      borderColor: PARTIAL_OVERLAP_COLORS.borderColor,
      icon: <SyncAltIcon sx={{ fontSize: 15, color: PARTIAL_OVERLAP_COLORS.iconColor, flexShrink: 0, mt: '1px' }} />,
      text: `Partially overlaps with existing blackout ${existingLabel}. Ignore the new dates, or extend the existing period to ${mergedLabel}.`,
      actions: (
        <Stack direction="row" gap={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onResolve('ignored')}
            sx={{
              fontSize: 11,
              borderColor: 'warning.400',
              color: 'warning.dark',
              '&:hover': { borderColor: 'warning.600', bgcolor: 'warning.50' },
            }}
          >
            Ignore new dates
          </Button>
          <Button
            size="small"
            variant="outlined"
            onClick={() => onResolve('extending')}
            sx={{
              fontSize: 11,
              borderColor: PARTIAL_OVERLAP_COLORS.borderColor,
              color: PARTIAL_OVERLAP_COLORS.textColor,
              '&:hover': {
                borderColor: PARTIAL_OVERLAP_COLORS.hoverBorderColor,
                bgcolor: PARTIAL_OVERLAP_COLORS.bgcolor,
              },
            }}
          >
            Extend to {mergedLabel}
          </Button>
        </Stack>
      ),
    },
  } as const;

  const c = config[conflict.type];

  return (
    <Box
      sx={{
        ml: 4,
        p: 1,
        pl: 1.5,
        borderRadius: 1,
        bgcolor: c.bgcolor,
        border: '1px solid',
        borderColor: c.borderColor,
      }}
    >
      <Stack direction="row" alignItems="flex-start" gap={0.75}>
        {c.icon}
        <Typography variant="caption">{c.text}</Typography>
      </Stack>
      <Box sx={{ mt: 0.75, pl: 2.5 }}>{c.actions}</Box>
    </Box>
  );
}
